import React, { useCallback, useMemo } from "react";
import { SelectionTable, VirtualTableStyles } from "@commonsku/styles";
import { Column } from "react-table";
import { useReportContext } from "../report_context";
import {
  mapRowDataByConfig,
  ReportColumnConfig,
  ReportRow,
} from "../columns/config/ReportColumnConfig";
import {
  BASE_COLUMN_TYPES,
  BaseColumnType,
  REPORT_TOOLTIP_TARGET_ID,
} from "../columns/config/types";
import Tooltip from "../../helpers/ReactTooltip";
import { rebuildTooltip } from "../../helpers";
import "./ReportTable.css";

export type StaticReportRow = Record<
  keyof ReportRow,
  ReportRow[keyof ReportRow]
>;

const NUMERIC_COLUMN_TYPES: BaseColumnType[] = [
  BASE_COLUMN_TYPES.Money,
  BASE_COLUMN_TYPES.Quantity,
  BASE_COLUMN_TYPES.Percentage,
  BASE_COLUMN_TYPES.Margin,
  BASE_COLUMN_TYPES.Commission,
];

const compare = (a: number | string, b: number | string) =>
  a < b ? -1 : a > b ? 1 : 0;

/**
 * react-table cannot sort these rows unaided: mapRowDataByConfig nests each column's fields under
 * its accessorKey, so the default comparators only ever see objects and treat every row as equal.
 * These reach in for the scalar. Null values compare as empty strings, so they lead ascending.
 */
const clientSortType = (column: ReportColumnConfig<BaseColumnType>) => {
  const { accessorKey, fields, orderByField, type } = column;
  const field = fields.includes(orderByField) ? orderByField : accessorKey;
  const valueOf = (row: { original: StaticReportRow }) =>
    (row.original[accessorKey] ?? {})[field];

  if (NUMERIC_COLUMN_TYPES.includes(type)) {
    return (rowA, rowB) =>
      compare(Number(valueOf(rowA)) || 0, Number(valueOf(rowB)) || 0);
  }

  if (type === BASE_COLUMN_TYPES.Date) {
    return (rowA, rowB) =>
      compare(Date.parse(valueOf(rowA)) || 0, Date.parse(valueOf(rowB)) || 0);
  }

  return (rowA, rowB) =>
    compare(
      String(valueOf(rowA) ?? "").toLowerCase(),
      String(valueOf(rowB) ?? "").toLowerCase(),
    );
};

interface StaticReportTableProps {
  rows: object[];
  selectedRows: StaticReportRow[];
  onSelectionChange: (rows: StaticReportRow[]) => void;
}

/**
 * A report table for rows the caller already holds, sorted in the browser.
 *
 * `ReportTable` can only draw what `ReportProvider` fetched, and only from the
 * `{ report, totals }` envelope. Use this instead when rows come from somewhere else — an endpoint
 * returning a plain array, or a fixture — and there is therefore no server-side sort or paging.
 * It must still be rendered inside a `ReportProvider`, since the cell components and the parsed
 * column config both come from that context.
 */
const StaticReportTable = ({
  rows,
  selectedRows,
  onSelectionChange,
}: StaticReportTableProps) => {
  const { columns, elementSizeParameters } = useReportContext();

  const sortableColumns = useMemo(
    () =>
      columns.map((column) => ({
        ...column,
        sortType: clientSortType(column),
      })),
    [columns],
  );

  const data = useMemo(
    () => mapRowDataByConfig(rows, columns) as StaticReportRow[],
    [rows, columns],
  );

  const tableHeight = data.length * elementSizeParameters.ROW_HEIGHT;

  const rowStyles = useCallback(
    () => ({ height: `${elementSizeParameters.ROW_HEIGHT}px` }),
    [elementSizeParameters.ROW_HEIGHT],
  );

  // Deliberately leaves `top` to react-window. ReportTable derives it from row.index, which is
  // safe only because reports sort on the server; with client-side sorting that index still points
  // at the row's original slot and would undo the sort visually.
  const rowGroupStyles = useCallback(
    () => ({
      height: `${elementSizeParameters.ROW_HEIGHT}px`,
      borderBottom: "1px solid rgba(178, 205, 209, 0.3)",
    }),
    [elementSizeParameters.ROW_HEIGHT],
  );

  return (
    <>
      <Tooltip
        className="report-table-tooltip"
        id={REPORT_TOOLTIP_TARGET_ID}
        isCapture
        delayHide={100}
        delayUpdate={100}
      />
      <VirtualTableStyles
        className="report-table-wrapper"
        tableHeight={tableHeight}
      >
        <SelectionTable
          columns={sortableColumns as unknown as Column<StaticReportRow>[]}
          data={data}
          height={tableHeight}
          itemSize={() => elementSizeParameters.ROW_HEIGHT}
          tableHeaderProps={{
            className: "report-table-header subreport-table-header",
          }}
          rowStyles={rowStyles}
          rowGroupStyles={rowGroupStyles}
          onScroll={rebuildTooltip}
          selectedRows={selectedRows}
          onSelectionChange={onSelectionChange}
        />
      </VirtualTableStyles>
    </>
  );
};

export default StaticReportTable;
